import { useEffect, useState } from "react";
import { api, fmtBytes, fmtNum, downloadFile, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Gauge, Coins, FileDown, HardDrive, Cpu, Zap, TrendingUp, FileText, Download } from "lucide-react";
import { cn } from "@/lib/utils";

const usd = (n) => `$${(n || 0).toFixed(n >= 1 ? 2 : 4)}`;
const usd2 = (n) => `$${(n || 0).toFixed(2)}`;

const BAR_COLORS = ["bg-cyan-500", "bg-amber-500", "bg-rose-500"];

function QuotaBar({ label, icon: Icon, used, limit, format, testid }) {
  const unlimited = !limit;
  const pct = unlimited ? 0 : Math.min(100, (used / limit) * 100);
  const tone = pct >= 90 ? 2 : pct >= 70 ? 1 : 0;
  return (
    <div className="rounded-md border border-border bg-[#0E1420] px-4 py-3 space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-slate-500" />
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
        <p className="ml-auto font-mono text-sm text-slate-200">
          {format(used)}
          <span className="text-slate-600"> / {unlimited ? "∞" : format(limit)}</span>
        </p>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
        <div className={cn("h-full rounded-full transition-all duration-500", BAR_COLORS[tone])}
          style={{ width: `${unlimited ? 2 : Math.max(pct, used > 0 ? 2 : 0)}%` }} />
      </div>
      {!unlimited && pct >= 90 && (
        <p className="text-[10px] font-mono text-rose-300">quota nearly exhausted — upgrade to keep going</p>
      )}
    </div>
  );
}

export default function UsagePanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const load = () => api.getUsage(30).then(setData).catch(() => {});
  useEffect(() => {
    load();
    api.getPlans().then((d) => setPlans(d.plans)).catch(() => {});
    api.listInvoices().then((d) => setInvoices(d.invoices)).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const grabInvoice = async (period) => {
    try {
      await downloadFile(`${API}/usage/invoice.pdf?period=${period}`, `dataforge-invoice-${period}.pdf`);
    } catch (e) {
      toast.error(e.message || "Could not download invoice");
    }
  };

  const l = data?.limits || {};
  const u = data?.used || {};
  const c = data?.counters || {};
  const tokens = (c.in_tokens || 0) + (c.out_tokens || 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[1100px]" data-testid="usage-panel">
      <header className="flex flex-wrap items-end gap-4">
        <div>
          <h2 className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">Usage &amp; Plan</h2>
          <p className="text-sm text-slate-500 mt-1">
            Tokens, AI spend, pages and storage metered per account — billing period {data?.period || "—"}.
          </p>
        </div>
        <span data-testid="usage-plan-badge"
          className="ml-auto px-3 py-1 rounded font-mono text-[11px] uppercase border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
          {data?.plan_label || "—"} plan
        </span>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "AI spend", value: usd2(u.llm_cost_usd), icon: Coins, testid: "usage-stat-cost" },
          { label: "Tokens", value: fmtNum(tokens), icon: Zap, testid: "usage-stat-tokens" },
          { label: "LLM calls", value: fmtNum(c.llm_calls || 0), icon: Cpu, testid: "usage-stat-calls" },
          { label: "Pages crawled", value: fmtNum(u.pages), icon: TrendingUp, testid: "usage-stat-pages" },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border bg-[#0B0F18] px-4 py-3" data-testid={s.testid}>
            <div className="flex items-center gap-1.5">
              <s.icon className="h-3.5 w-3.5 text-cyan-400" />
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{s.label}</p>
            </div>
            <p className="font-mono font-bold text-xl mt-1.5">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Gauge className="h-4 w-4 text-cyan-400" />
          <h3 className="font-display font-bold text-base">Quotas this month</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <QuotaBar testid="quota-pages" label="Crawl pages" icon={TrendingUp} used={u.pages || 0}
            limit={l.pages_per_month} format={fmtNum} />
          <QuotaBar testid="quota-cost" label="AI spend" icon={Coins} used={u.llm_cost_usd || 0}
            limit={l.llm_cost_usd_per_month} format={usd} />
          <QuotaBar testid="quota-exports" label="Exports saved" icon={FileDown} used={u.exports || 0}
            limit={l.exports_per_month} format={fmtNum} />
          <QuotaBar testid="quota-storage" label="Storage" icon={HardDrive} used={u.storage_bytes || 0}
            limit={l.storage_bytes} format={fmtBytes} />
        </div>
        <p className="text-[11px] font-mono text-slate-600">
          Hard limits: crawls stop and exports are refused once a quota is hit.
          {user?.role === "owner" ? " As the owner you are on the unlimited plan and can raise any tenant's limits in Settings → Team." : " Ask the owner to raise your limits."}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-[#0B0F18] p-5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-3">AI spend · last 30 days</p>
          <div className="h-[190px]" data-testid="usage-cost-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.daily || []}>
                <defs>
                  <linearGradient id="costFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#475569", fontSize: 9 }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fill: "#475569", fontSize: 9 }} width={46} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={{ background: "#0B0F18", border: "1px solid #1e293b", fontSize: 12 }}
                  formatter={(v) => usd(v)} />
                <Area type="monotone" dataKey="cost_usd" stroke="#22d3ee" fill="url(#costFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-[#0B0F18] p-5">
          <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-3">Pages crawled · last 30 days</p>
          <div className="h-[190px]" data-testid="usage-pages-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.daily || []}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#475569", fontSize: 9 }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fill: "#475569", fontSize: 9 }} width={46} />
                <Tooltip contentStyle={{ background: "#0B0F18", border: "1px solid #1e293b", fontSize: 12 }} />
                <Bar dataKey="pages" fill="#34d399" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-3" data-testid="usage-by-model">
        <h3 className="font-display font-bold text-base">Spend by model</h3>
        {(data?.by_model || []).length === 0 ? (
          <p className="text-xs font-mono text-slate-600">
            No AI calls metered yet — run the Synthesizer with one of your provider keys and token costs land here.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-wider text-slate-600">
                  <th className="py-2 pr-4">Provider</th><th className="py-2 pr-4">Model</th>
                  <th className="py-2 pr-4">Calls</th><th className="py-2 pr-4">In</th>
                  <th className="py-2 pr-4">Out</th><th className="py-2">Cost</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {data.by_model.map((m) => (
                  <tr key={`${m.provider}-${m.model}`} className="border-t border-border/60">
                    <td className="py-2 pr-4 text-cyan-300">{m.provider}</td>
                    <td className="py-2 pr-4 text-slate-300">{m.model}</td>
                    <td className="py-2 pr-4">{fmtNum(m.calls)}</td>
                    <td className="py-2 pr-4">{fmtNum(m.in_tokens)}</td>
                    <td className="py-2 pr-4">{fmtNum(m.out_tokens)}</td>
                    <td className="py-2 text-emerald-300">{usd(m.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-3" data-testid="invoices-card">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-cyan-400" />
          <h3 className="font-display font-bold text-base">Invoices</h3>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-slate-600">
            plan {usd2(plans.find((p) => p.id === data?.plan)?.price_usd || 0)}/mo + metered AI
          </span>
        </div>
        <p className="text-xs text-slate-500 -mt-1">
          Every billing period becomes a PDF statement with pages crawled, token counts and AI spend by model.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-wider text-slate-600">
                <th className="py-2 pr-4">Period</th><th className="py-2 pr-4">Pages</th>
                <th className="py-2 pr-4">Tokens</th><th className="py-2 pr-4">AI spend</th>
                <th className="py-2 pr-4">Total</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody className="font-mono text-xs">
              {invoices.map((iv) => (
                <tr key={iv.period} className="border-t border-border/60" data-testid={`invoice-row-${iv.period}`}>
                  <td className="py-2 pr-4 text-slate-200">{iv.period}</td>
                  <td className="py-2 pr-4">{fmtNum(iv.pages)}</td>
                  <td className="py-2 pr-4">{fmtNum(iv.tokens)}</td>
                  <td className="py-2 pr-4 text-emerald-300">{usd(iv.ai_cost_usd)}</td>
                  <td className="py-2 pr-4">{usd2(iv.total_usd)}</td>
                  <td className="py-2">
                    <Button size="sm" variant="ghost" data-testid={`download-invoice-${iv.period}`}
                      onClick={() => grabInvoice(iv.period)}
                      className="h-7 text-cyan-300 hover:text-cyan-200 text-[11px]">
                      <Download className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3" data-testid="plans-grid">
        {plans.map((p) => (
          <div key={p.id} className={cn("rounded-lg border p-5 space-y-2",
            p.id === data?.plan ? "border-cyan-500/50 bg-cyan-500/5" : "border-border bg-[#0B0F18]")}>
            <div className="flex items-center justify-between">
              <p className="font-display font-bold">{p.label}</p>
              <p className="font-mono text-xs text-slate-400">{p.price_usd ? `$${p.price_usd}/mo` : "—"}</p>
            </div>
            <ul className="space-y-1 text-[11px] font-mono text-slate-500">
              <li>{p.pages_per_month ? `${fmtNum(p.pages_per_month)} pages/mo` : "unlimited pages"}</li>
              <li>{p.llm_cost_usd_per_month ? `${usd(p.llm_cost_usd_per_month)} AI spend/mo` : "unmetered AI spend"}</li>
              <li>{p.active_jobs} concurrent job{p.active_jobs > 1 ? "s" : ""}</li>
              <li>{fmtBytes(p.storage_bytes)} storage</li>
            </ul>
            {p.id === data?.plan && (
              <p className="text-[10px] font-mono uppercase tracking-wider text-cyan-300">current plan</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
